/**
 * Widget SSO authentication utilities.
 *
 * Flow: Widget opens system browser → user logs in (SSO or password) →
 * JWT redirected back to widget via recare-widget:// custom protocol.
 *
 * sessionStorage is used to persist the widget flag across the SSO redirect
 * chain (Frontend → FusionAuth → IdP → Backend → Frontend) since it survives
 * cross-origin navigations within the same tab.
 */

import { getQueryBooleanVariable } from "core/model/utils/urls";

const WIDGET_SESSION_KEY = "widget_auth_pending";
const WIDGET_PROTOCOL = "recare-widget";

/**
 * Detect ?widget query param and persist in sessionStorage.
 * Called once on login page mount. The param is removed from the URL
 * to prevent re-triggering on refresh.
 */
export function initWidgetAuthFlow(): void {
  if (!getQueryBooleanVariable(window.location.search, "widget")) return;

  sessionStorage.setItem(WIDGET_SESSION_KEY, "true");

  const params = new URLSearchParams(window.location.search);
  params.delete("widget");
  const query = params.toString();
  const cleanUrl = `${window.location.pathname}${query ? `?${query}` : ""}`;
  window.history.replaceState({}, "", cleanUrl);
}

/** Check if the current session is a widget authentication flow */
export function isWidgetAuthFlow(): boolean {
  return sessionStorage.getItem(WIDGET_SESSION_KEY) === "true";
}

/**
 * Redirect JWT to widget via custom protocol handler.
 * Uses a hidden iframe to trigger the protocol without navigating
 * the page, then shows a "you can close this tab" message.
 */
export function redirectToWidget(
  token: string,
  title: string,
  message: string,
): void {
  sessionStorage.removeItem(WIDGET_SESSION_KEY);
  window.history.replaceState({}, "", "/auth");

  // Trigger custom protocol via anchor click — works on both HTTP and HTTPS.
  // Hidden iframes are silently blocked by Chrome/Safari on HTTPS pages.
  const a = document.createElement("a");
  a.href = `${WIDGET_PROTOCOL}://callback?token=${encodeURIComponent(token)}`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  // Show a clean message after the protocol redirect fires.
  // Consistent for both SSO and password login — user closes the tab manually.
  setTimeout(() => {
    document.title = title;
    document.body.innerHTML =
      '<div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;color:#666">' +
      `<p>${message}</p></div>`;
  }, 500);
}
